import { useState, useRef, useEffect } from "react";

interface UseAudioRecording {
  isRecording: boolean;
  isPaused: boolean;
  audioBlob: Blob | null;
  timer: string; // Timer is now a string in 'HH:mm:ss' or 'mm:ss' format
  startRecording: () => void;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  deleteRecording: () => void;
}

function useAudioRecording(): UseAudioRecording {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [timer, setTimer] = useState<string>("00:00"); // Timer state as string in 'mm:ss' or 'HH:mm:ss'
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const intervalRef = useRef<number | null>(null); // Reference for the timer interval (use `number` in browser)
  const startTimeRef = useRef<number>(0); // Keeps track of the time when recording started or resumed
  const elapsedBeforePauseRef = useRef<number>(0); // Keeps track of the elapsed time before the recording is paused

  // Function to format time into 'HH:mm:ss' or 'mm:ss'
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600); // Total hours
    const minutes = Math.floor((seconds % 3600) / 60); // Remaining minutes
    const remainingSeconds = seconds % 60; // Remaining seconds

    if (hours >= 1) {
      // Format as 'HH:mm:ss'
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
    } else {
      // Format as 'mm:ss'
      return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
        .toString()
        .padStart(2, "0")}`;
    }
  };

  // Function to start the timer
  const startTimer = () => {
    startTimeRef.current = Date.now() - elapsedBeforePauseRef.current * 1000;
    intervalRef.current = window.setInterval(() => {
      const secondsElapsed = Math.floor(
        (Date.now() - startTimeRef.current) / 1000
      );
      setTimer(formatTime(secondsElapsed)); // Update timer display with formatted time
    }, 1000); // Update every second
  };

  // Function to stop the timer
  const stopTimer = () => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current); // Clear the interval when stopped
    }
  };

  // Function to start recording
  const startRecording = () => {
    audioChunksRef.current = [];
    setIsRecording(true);
    setIsPaused(false);
    setTimer("00:00"); // Reset the timer when a new recording starts
    elapsedBeforePauseRef.current = 0; // Reset the pause time

    // Request audio input from the user's microphone
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        // Create MediaRecorder for the audio stream
        const mimeType = MediaRecorder.isTypeSupported("audio/mpeg")
          ? "audio/mpeg"
          : "audio/webm";
        mediaRecorderRef.current = new MediaRecorder(stream, {
          mimeType: mimeType,
        });

        mediaRecorderRef.current.ondataavailable = (event: BlobEvent) => {
          audioChunksRef.current.push(event.data);
        };

        mediaRecorderRef.current.onstop = () => {
          const blob = new Blob(audioChunksRef.current, { type: "audio/mpeg" });
          setAudioBlob(blob); // Store the audio blob to use later
          stopTimer(); // Stop the timer when recording stops
        };

        mediaRecorderRef.current.start();
        startTimer(); // Start the timer when recording starts
      })
      .catch((err: Error) => {
        console.error("Error accessing microphone: ", err);
        setIsRecording(false);
      });
  };

  // Function to stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      const stream = mediaRecorderRef.current.stream;
      stream.getTracks().forEach((track) => track.stop());
      mediaRecorderRef.current = null;
      stopTimer();
      setTimer("00:00");
      setIsRecording(false);
    }
  };

  // Function to pause recording
  const pauseRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      elapsedBeforePauseRef.current = Math.floor(
        (Date.now() - startTimeRef.current) / 1000
      ); // Track elapsed time before pause
      stopTimer(); // Stop the timer when paused
    }
  };

  // Function to resume recording
  const resumeRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "paused"
    ) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      startTimer(); // Resume the timer where it left off
    }
  };

  // Function to delete recording
  const deleteRecording = () => {
    stopTimer();
    setAudioBlob(null);
    setTimer("00:00");
  };

  // Clean up when the component is unmounted or recording stops
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current); // Clear the timer on unmount
      }
    };
  }, []);

  return {
    isRecording,
    isPaused,
    audioBlob,
    timer,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    deleteRecording,
  };
}

export default useAudioRecording;
